import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useContent } from '../../hooks/useContent';

const Footer = () => {
  const { t, getLocalizedPath } = useLanguage();
  const { data: contactData } = useContent('contact_info');
  const contact = contactData?.[0];

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-slate-300 pt-16 pb-8">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Column */}
          <div className="space-y-6">
            <img 
              src="https://assets.auxodev.dk/assets/488e7d70-fe21-4956-bd96-e0986a28cf0b/b13cc169-9c47-4994-b3be-a5508b3472d5.png" 
              alt="Rail & Maskinteknik Logo" 
              className="h-[75px] w-auto"
            />
            <p className="text-sm leading-relaxed">
              {t({
                da: 'Specialiseret service og teknisk rådgivning inden for skinnekørende arbejdsmaskiner og Yellow Fleet.',
                en: 'Specialized service and technical advisory within rail-bound work machines and Yellow Fleet.',
                de: 'Spezialisierter Service und technische Beratung für schienengebundene Arbeitsmaschinen und Yellow Fleet.'
              })}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold mb-6 uppercase tracking-wider text-sm">
              {t({ da: 'Hurtige links', en: 'Quick Links', de: 'Schnelllinks' })}
            </h4>
            <ul className="space-y-4 text-sm">
              <li><Link to={getLocalizedPath('/service')} className="hover:text-white transition-colors">Service</Link></li>
              <li><Link to={getLocalizedPath('/teknisk-radgivning')} className="hover:text-white transition-colors">{t({ da: 'Teknisk rådgivning', en: 'Technical Advisory', de: 'Technische Beratung' })}</Link></li>
              <li><Link to={getLocalizedPath('/salg-og-kob')} className="hover:text-white transition-colors">{t({ da: 'Salg og køb', en: 'Sales & Purchase', de: 'Verkauf & Kauf' })}</Link></li>
              <li><Link to={getLocalizedPath('/ecm-certifikat')} className="hover:text-white transition-colors">ECM {t({ da: 'Certifikat', en: 'Certificate', de: 'Zertifikat' })}</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-bold mb-6 uppercase tracking-wider text-sm">
              {t({ da: 'Kontakt', en: 'Contact', de: 'Kontakt' })}
            </h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-slate-500" />
                <a href="tel:+4521590019" className="hover:text-white transition-colors">
                  21 59 00 19
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-slate-500" />
                <a href="mailto:kra@railmaskinteknik.dk" className="hover:text-white transition-colors">
                  kra@railmaskinteknik.dk
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-slate-500" />
                <span>{contact ? t(contact.address) : 'Rail & Maskinteknik ApS, Danmark'}</span>
              </li>
              <li className="flex items-center gap-3">
                <ShieldCheck className="w-4 h-4 text-slate-500" />
                <span>CVR: {contact?.cvr || '44117568'}</span>
              </li>
            </ul>
          </div>

          {/* Certification */}
          <div>
            <h4 className="text-white font-bold mb-6 uppercase tracking-wider text-sm">
              {t({ da: 'Certificering', en: 'Certification', de: 'Zertifizierung' })}
            </h4>
            <div className="bg-white/5 p-4 rounded-lg border border-white/10">
              <p className="text-xs italic mb-2">ECM Certified Entity</p>
              <div className="h-1 w-full bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 w-full"></div>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-500">
          <p>© {currentYear} Rail & Maskinteknik ApS. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
